import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import {
  EnumUserStatus,
  EnumUserUserType,
  useUserCreateMutation,
  useUserUpdateMutation,
} from "gql/generated";
import { emailRegex } from "shared/utils";
import { useOnboardingContext } from "onboarding/OnboardingContext.tsx";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Onboarding]);

export const createOrUpdateUserSchema = z.object({
  firstName: z
    .string({
      required_error: `${i18n.t("onboarding:FORM.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("onboarding:FORM.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  lastName: z
    .string({
      required_error: `${i18n.t("onboarding:FORM.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("onboarding:FORM.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  email: z
    .string()
    .regex(emailRegex, { message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL") }),
});

export type CreateOrUpdateUserFormValues = z.infer<
  typeof createOrUpdateUserSchema
>;

type UseCreateOrUpdateUserFormProps = {
  onSuccess: (passwordResetUrl: string) => void;
  onError: () => void;
};

export const useCreateOrUpdateUserForm = ({
  onSuccess,
  onError,
}: UseCreateOrUpdateUserFormProps) => {
  const { user, emailAddress, organization } = useOnboardingContext();

  const defaultValues: CreateOrUpdateUserFormValues = {
    firstName: user ? user.firstName : "",
    lastName: user ? user.lastName : "",
    email: emailAddress ?? "",
  };

  const [createUser, { loading: createUserLoading }] = useUserCreateMutation();
  const [updateUser, { loading: updateUserLoading }] = useUserUpdateMutation();

  const formHandler = useFormHandler<CreateOrUpdateUserFormValues>(
    useCallback(
      async (data) => {
        if (data) {
          const isSignupFormDirtyForExistingUser =
            (data.firstName &&
              data.firstName.length > 0 &&
              data.firstName !== user?.firstName) ||
            (data.lastName &&
              data.lastName.length > 0 &&
              data.lastName &&
              data.lastName !== user?.lastName);

          if (user) {
            if (isSignupFormDirtyForExistingUser) {
              /* User exists and signup form has been edited. Update the user */
              await updateUser({
                variables: {
                  id: user._id,
                  record: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                  },
                },
              })
                .then((res) => {
                  const user = res.data?.userUpdate;
                  if (user && user.passwordResetUrl) {
                    onSuccess(user.passwordResetUrl);
                  } else {
                    onError();
                  }
                })
                .catch(() => {
                  onError();
                });
            } else if (user.passwordResetUrl) {
              /* User exists and signup form hasn't been edited. Navigate to password reset URL */
              onSuccess(user.passwordResetUrl);
            } else {
              onError();
            }
          } else {
            /**
             * User does not exist. Create account
             * This would only happen on cold signup, so `access` will be empty
             */
            await createUser({
              variables: {
                record: {
                  email: data.email ?? "",
                  firstName: data.firstName ?? "",
                  lastName: data.lastName ?? "",
                  access: { operators: [], organizations: [], storefronts: [] },
                  status: EnumUserStatus.Provisioned,
                  userType: EnumUserUserType.Whereabouts,
                  onboardingMetadata: {
                    onboardingOrganization: organization?._id,
                  },
                },
              },
            })
              .then((res) => {
                const user = res.data?.userCreate;
                if (user && user.passwordResetUrl) {
                  onSuccess(user.passwordResetUrl);
                } else {
                  onError();
                }
              })
              .catch(() => {
                onError();
              });
          }
        }
      },
      [createUser, onError, onSuccess, organization, updateUser, user],
    ),
    defaultValues,
    { schema: createOrUpdateUserSchema, mode: "onBlur" },
  );

  return { ...formHandler, loading: createUserLoading || updateUserLoading };
};
